import React, { useEffect, useMemo, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import { fetchPartsApi } from "../components/Catalogue/Api/partsApi";

export default function AsyncAutocomplete({
  fetchUrl,
  value,
  onChange,
  label = "Select",
  placeholder = "Type to search…",
  debounceMs = 300,
  minSearchLength = 1,
  getOptionLabel = (o) => o.label,
  disabled = false,
}) {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Debounce with useEffect + timeout
  useEffect(() => {
    if (inputValue.length < minSearchLength) {
      setOptions([]);
      setLoading(false);
      setError(null);
      return;
    }

    const controller = new AbortController();
    const signal = controller.signal;
    const id = window.setTimeout(() => {
      setLoading(true);
      setError(null);

      fetchPartsApi(`?globalFilter=${inputValue}`)
        .then((res) => {
          setOptions(res?.data);
        })
        .catch((err) => {
          if (err.name === "AbortError") return;
          console.error(err);
          setError("Failed to fetch options");
        })
        .finally(() => {
          setLoading(false);
        });
    }, debounceMs);

    return () => {
      clearTimeout(id);
      controller.abort();
    };
  }, [inputValue, fetchUrl, debounceMs, minSearchLength]);

  // optional: memoize renderOptionLabel for performance
  const optionLabel = useMemo(() => getOptionLabel, [getOptionLabel]);

  return (
    <Autocomplete
      disabled={disabled}
      options={options}
      getOptionLabel={(opt) => (opt ? optionLabel(opt) : "")}
      filterOptions={(options, state) => {
        const q = (state.inputValue || "").trim().toLowerCase();
        if (!q) return options;

        return options.filter((o) => {
          const part = (o?.PARTNAME ?? "").toString().toLowerCase();
          const dex = (o?.DEXT_PARTBARCODE ?? "").toString().toLowerCase();
          const bar = (o?.BARCODE ?? "").toString().toLowerCase();

          return part.includes(q) || dex.includes(q) || bar.includes(q);
        });
      }}
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      inputValue={inputValue}
      onInputChange={(_, newInput) => setInputValue(newInput)}
      loading={loading}
      isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
      noOptionsText={
        inputValue.length < minSearchLength
          ? `Type at least ${minSearchLength} character(s)`
          : loading
          ? "Loading..."
          : error ?? "No options"
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          helperText={error ?? ""}
          error={Boolean(error)}
        />
      )}
    />
  );
}
